import { createClient } from '@/lib/supabase/client';
import { emailService } from './emailService';

export interface Appointment {
  id: string;
  userId: string;
  fullName: string;
  email: string;
  phone: string;
  appointmentDate: string;
  appointmentTime: string;
  location: string;
  notes?: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  createdAt: string;
  updatedAt: string;
}

export interface AppointmentRequest {
  fullName: string;
  email: string;
  phone: string;
  appointmentDate: string;
  appointmentTime: string;
  location: string;
  notes?: string;
}

class AppointmentService {
  private supabase = createClient();

  async createAppointment(data: AppointmentRequest): Promise<boolean> {
    try {
      const { data: { user } } = await this.supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await this.supabase
        .from('appointments')
        .insert([{
          user_id: user.id,
          full_name: data.fullName,
          email: data.email,
          phone: data.phone,
          appointment_date: data.appointmentDate,
          appointment_time: data.appointmentTime,
          location: data.location,
          notes: data.notes,
          status: 'pending'
        }]);

      if (error) throw error;

      // Send confirmation email to customer
      await emailService.sendAppointmentConfirmationToCustomer({
        email: data.email,
        fullName: data.fullName,
        appointmentDate: data.appointmentDate,
        appointmentTime: data.appointmentTime,
        location: data.location,
        notes: data.notes,
      });

      // Send notification email to admin
      const adminEmail = 'admin@containersbyzip.com';
      const managerPortalUrl = `${window.location.origin}/manager-portal`;
      await emailService.sendAppointmentConfirmationToAdmin({
        adminEmail,
        fullName: data.fullName,
        email: data.email,
        phone: data.phone,
        appointmentDate: data.appointmentDate,
        appointmentTime: data.appointmentTime,
        location: data.location,
        notes: data.notes,
        managerPortalUrl,
      });

      return true;
    } catch (error) {
      console.error('Error creating appointment:', error);
      return false;
    }
  }

  async getUserAppointments(): Promise<Appointment[]> {
    try {
      // First check if we have a valid session
      const { data: { session }, error: sessionError } = await this.supabase.auth.getSession();
      
      if (sessionError || !session) {
        console.error('No valid session found:', sessionError);
        return []; // Return empty array instead of throwing error
      }

      const { data, error } = await this.supabase
        .from('appointments')
        .select('*')
        .eq('user_id', session.user.id)
        .order('appointment_date', { ascending: true });

      if (error) throw error;

      return (data || []).map(apt => ({
        id: apt.id,
        userId: apt.user_id,
        fullName: apt.full_name,
        email: apt.email,
        phone: apt.phone,
        appointmentDate: apt.appointment_date,
        appointmentTime: apt.appointment_time,
        location: apt.location,
        notes: apt.notes,
        status: apt.status,
        createdAt: apt.created_at,
        updatedAt: apt.updated_at
      }));
    } catch (error) {
      console.error('Error fetching appointments:', error);
      return [];
    }
  }

  async getAllAppointments(): Promise<Appointment[]> {
    try {
      const { data, error } = await this.supabase
        .from('appointments')
        .select('*')
        .order('appointment_date', { ascending: true });

      if (error) throw error;

      return (data || []).map(apt => ({
        id: apt.id,
        userId: apt.user_id,
        fullName: apt.full_name,
        email: apt.email,
        phone: apt.phone,
        appointmentDate: apt.appointment_date,
        appointmentTime: apt.appointment_time,
        location: apt.location,
        notes: apt.notes,
        status: apt.status,
        createdAt: apt.created_at,
        updatedAt: apt.updated_at
      }));
    } catch (error) {
      console.error('Error fetching all appointments:', error);
      return [];
    }
  }

  async updateAppointmentStatus(appointmentId: string, status: 'pending' | 'confirmed' | 'cancelled' | 'completed'): Promise<boolean> {
    try {
      const { error } = await this.supabase
        .from('appointments')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', appointmentId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error updating appointment status:', error);
      return false;
    }
  }

  async cancelAppointment(appointmentId: string): Promise<boolean> {
    return this.updateAppointmentStatus(appointmentId, 'cancelled');
  }
}

export const appointmentService = new AppointmentService();